
import { useState } from 'react';
import { useReferralCode } from '@/hooks/affiliates/useReferralCode';
import { useCampaignLinks } from '@/hooks/affiliates/useCampaignLinks';
import { useToast } from '@/hooks/use-toast';
import { ReferralLinkCard } from './referral-tools/ReferralLinkCard';
import { SharingToolsCard } from './referral-tools/SharingToolsCard';
import { CampaignLinksTable } from './referral-tools/CampaignLinksTable';
import { LoadingState } from './referral-tools/LoadingState';
import { ErrorState } from './referral-tools/ErrorState';

interface ReferralToolsProps {
  userId?: string;
}

export function ReferralTools({ userId }: ReferralToolsProps) {
  const { toast } = useToast();
  const { referralUrl, loading, error } = useReferralCode(userId);
  const {
    campaignLinks,
    loading: campaignLoading,
    error: campaignError,
    createCampaignLink,
    deleteCampaignLink
  } = useCampaignLinks(userId);
  const [isCopying, setIsCopying] = useState(false);


  
  const handleCopyLink = async () => {
    if (!referralUrl) {
      toast({
        title: "No link available",
        description: "Please wait while we generate your referral link.",
        variant: "destructive"
      });
      return;
    }
    
    setIsCopying(true);
    try {
      await navigator.clipboard.writeText(referralUrl);
      toast({
        title: "Link copied to clipboard",
        description: "Your referral link has been copied to your clipboard."
      });
    } catch (err) {
      toast({
        title: "Failed to copy",
        description: "Could not copy to clipboard. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsCopying(false);
    }
  };
  
  const handleShare = async () => {
    if (!referralUrl) {
      toast({
        title: "No link available",
        description: "Please wait while we generate your referral link.",
        variant: "destructive"
      });
      return;
    }
    
    const shareData = {
      title: 'The Mat Monster BJJ App',
      text: `I've been training BJJ with this amazing app! Check out The Mat Monster and get 30% off with my link: ${referralUrl}`,
      url: referralUrl,
    };

    // Check if Web Share API is supported
    if (navigator.share && typeof navigator.share === 'function') {
      try {
        await navigator.share(shareData);
        toast({
          title: "Shared successfully",
          description: "Thanks for sharing The Mat Monster!"
        });
      } catch (error: unknown) {
        // If the user cancels the share, it's not an error
        if (!(error instanceof Error) || error.name !== 'AbortError') {
          console.error('Error sharing:', error);
          // Fallback to copy method
          await handleCopyLink();
          toast({
            title: "Link copied instead",
            description: "We've copied the link to your clipboard since sharing wasn't possible."
          });
        }
      }
    } else {
      // Fallback for browsers that don't support the Web Share API
      await handleCopyLink();
      toast({
        title: "Link copied to clipboard",
        description: "Your browser doesn't support direct sharing. We've copied the link so you can share it manually."
      });
    }
  };
  
  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }
  
  return (
    <div className="space-y-6">
      <ReferralLinkCard
        referralUrl={referralUrl}
        isCopying={isCopying}
        onCopyLink={handleCopyLink}
        onShare={handleShare}
        createCampaignLink={createCampaignLink}
      />

      <SharingToolsCard referralUrl={referralUrl} />

      <CampaignLinksTable
        campaignLinks={campaignLinks}
        loading={campaignLoading}
        error={campaignError}
        deleteCampaignLink={deleteCampaignLink}
      />
    </div>
  );
}
